import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { usePlanningPeriod } from "@/context/PlanningPeriodContext";
import { PlanningPeriodSelector } from "@/components/PlanningPeriodSelector";
import { STATUS, WARN } from "@/lib/status";
import { fmtDateLong } from "@/lib/format";
import { ServiceCalendar } from "@/components/ServiceCalendar";
import { BoardStats } from "@/components/BoardStats";
import { useUI } from "@/context/UIContext";
import { AvailabilityReminder } from "@/components/AvailabilityReminder";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, X, Wand2, Clock, MapPin, Info, MessageSquare, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const AVAIL_RANK = { preferred: 0, available: 1, reluctant: 2, unavailable: 3, none: 4 };

export default function PlanningBoard() {
  const { period, setPeriod, query } = usePlanningPeriod();
  const { sidebarHidden, toggleSidebar } = useUI();
  const [services, setServices] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [availMap, setAvailMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [override, setOverride] = useState(null); // {payload, warnings}
  const [proposals, setProposals] = useState(null);
  const [busy, setBusy] = useState(false);

  const activeRef = useRef(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, v, a, av] = await Promise.all([
        api.get(`/services?${query}`),
        api.get("/users"),
        api.get(`/planning/assignments?${query}`),
        api.get(`/availability/overview?${query}`),
      ]);
      if (!activeRef.current) return;
      setServices(s.data.filter((service) => service.date >= period.date_from && service.date <= period.date_to));
      setVolunteers(v.data.filter((volunteer) => volunteer.active && !volunteer.invited && volunteer.role !== "admin"));
      setAssignments(a.data);
      setAvailMap(av.data);
    } catch (e) {
      if (activeRef.current) toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      if (activeRef.current) setLoading(false);
    }
  };
  useEffect(() => {
    activeRef.current = true;
    setProposals(null); setOverride(null); load();
    return () => { activeRef.current = false; };
  }, [query]);

  const slotAssignments = (svcId, slotId) =>
    assignments.filter((a) => a.service_id === svcId && a.slot_id === slotId);

  const doAssign = async (payload, ov = false) => {
    if (assigning) return;
    setAssigning(true);
    try {
      const { data } = await api.post("/planning/assignments", { ...payload, override: ov });
      setAssignments((prev) => [...prev, data.assignment]);
      const errs = (data.warnings || []).filter((w) => w.level !== "info");
      if (errs.length) toast.warning(errs.map((w) => w.message).join(" "));
      else toast.success("Ingepland");
      setOverride(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 409 && detail?.warnings) {
        setOverride({ payload, warnings: detail.warnings });
      } else {
        toast.error(formatApiError(detail));
      }
    } finally {
      setAssigning(false);
    }
  };

  const selectEmployee = (svc, slot, userId) => {
    if (assignments.some((a) => a.service_id === svc.id && a.user_id === userId)) return;
    doAssign({
      service_id: svc.id,
      slot_id: slot.id,
      service_type_id: slot.service_type_id,
      service_type_name: slot.service_type_name,
      user_id: userId,
    });
  };

  const removeAssignment = async (id) => {
    try {
      await api.delete(`/planning/assignments/${id}`);
      setAssignments((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/auto-suggest?${query}`);
      setProposals(data.proposals);
      if (data.count === 0) toast.info("Geen extra voorstellen mogelijk");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const applyProposals = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/planning/apply-suggestion?${query}`, { proposals });
      toast.success(`${data.created} diensten ingepland${data.skipped ? ` · ${data.skipped} inmiddels gewijzigde voorstellen overgeslagen` : ""}`);
      setProposals(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const availEntry = (svcId, userId) => (availMap[svcId] || {})[userId] || null;
  const availFor = (svcId, userId) => availEntry(svcId, userId)?.status || "none";

  return (
    <div className="space-y-5" data-testid="planning-board">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Planbord</h1>
          <p className="text-muted-foreground mt-1" data-testid="planning-instructions">Klik op een dienst in de kalender en plan medewerkers per functie in.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={toggleSidebar} className="hidden lg:inline-flex" data-testid="board-toggle-menu">
            {sidebarHidden ? <PanelLeftOpen className="w-4 h-4 mr-2" /> : <PanelLeftClose className="w-4 h-4 mr-2" />}
            {sidebarHidden ? "Menu tonen" : "Menu verbergen"}
          </Button>
          <Button onClick={generate} disabled={busy || loading || !services.length} data-testid="auto-suggest-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
            Planning voorstellen
          </Button>
        </div>
      </div>

      <PlanningPeriodSelector />
      <div className="flex flex-col lg:flex-row gap-5" data-testid="board-layout">
        <aside className="lg:w-80 shrink-0 lg:sticky lg:top-20 lg:self-start" data-testid="board-stats-col">
          <BoardStats volunteers={volunteers} assignments={assignments} />
        </aside>
        <div className="flex-1 min-w-0">
      <ServiceCalendar services={services} calendarId="board" loading={loading} periodStart={period.date_from} onPeriodChange={setPeriod}
        getStatus={(svc) => {
          const have = assignments.filter((a) => a.service_id === svc.id).length;
          const need = svc.slots.reduce((sum, slot) => sum + slot.desired, 0);
          return { tone: have >= need ? "red" : "amber", label: `${have}/${need} ingepland` };
        }}
        getNames={(svc) => assignments.filter((a) => a.service_id === svc.id).map((a) => {
          const st = STATUS[availFor(svc.id, a.user_id)] || STATUS.none;
          return { name: a.user_name, badge: st.badge };
        })}
        renderDetails={(svc) => (
                  <Card
                    key={svc.id}
                    data-testid={`board-service-${svc.id}`}
                    className="p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{svc.name}</p>
                        <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5">
                          <Clock className="w-3 h-3" /> {svc.start_time}–{svc.end_time}
                          {svc.location && <><MapPin className="w-3 h-3 ml-1" /> {svc.location}</>}
                        </p>
                        {svc.notes && <p className="text-xs text-amber-700 mt-1 flex items-start gap-1" data-testid={`board-note-${svc.id}`}><Info className="w-3 h-3 mt-0.5 shrink-0" />{svc.notes}</p>}
                      </div>
                    </div>
                    <div className="space-y-2 mt-3">
                      {svc.slots.map((slot) => {
                        const assigned = slotAssignments(svc.id, slot.id);
                        const need = slot.desired;
                        const ok = assigned.length >= need;
                        return (
                          <div
                            key={slot.id}
                            data-testid={`slot-${slot.id}`}
                            className="rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-semibold">{slot.service_type_name}</span>
                              <span data-testid={`slot-occupancy-${slot.id}`} className={`text-xs font-bold ${ok ? "text-red-600" : "text-amber-600"}`}>
                                {assigned.length}/{need}
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {assigned.map((a) => (
                                <span key={a.id} className="inline-flex items-center gap-1 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 text-xs px-2 py-1 rounded-full" data-testid={`assigned-${a.id}`}>
                                  {a.user_name}
                                  <button onClick={(e) => { e.stopPropagation(); removeAssignment(a.id); }} aria-label={`${a.user_name} uit dienst verwijderen`} disabled={assigning} data-testid={`remove-${a.id}`} className="hover:text-rose-600 disabled:opacity-50">
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                              {assigned.length === 0 && <span className="text-xs text-muted-foreground" data-testid={`empty-slot-${slot.id}`}>Nog niemand ingepland</span>}
                            </div>
                            <Select value="" onValueChange={(userId) => selectEmployee(svc, slot, userId)} disabled={assigning || busy || !!override || !volunteers.length}>
                              <SelectTrigger className="mt-3 bg-background" aria-label={`Medewerker inplannen voor ${svc.name}, ${slot.service_type_name}`} data-testid={`assign-select-${slot.id}`}>
                                <SelectValue placeholder={assigning ? "Bezig met inplannen…" : volunteers.length ? "Medewerker inplannen…" : "Geen actieve medewerkers"} />
                              </SelectTrigger>
                              <SelectContent data-testid={`assign-options-${slot.id}`} className="max-w-[calc(100vw-2rem)]">
                                {[...volunteers].sort((a, b) =>
                                  (AVAIL_RANK[availFor(svc.id, a.id)] ?? 4) - (AVAIL_RANK[availFor(svc.id, b.id)] ?? 4)
                                  || a.name.localeCompare(b.name)
                                ).map((v) => {
                                  const alreadyAssigned = assignments.some((a) => a.service_id === svc.id && a.user_id === v.id);
                                  const status = STATUS[availFor(svc.id, v.id)] || STATUS.none;
                                  return (
                                    <SelectItem key={v.id} value={v.id} disabled={alreadyAssigned} textValue={v.name} data-testid={`assign-option-${slot.id}-${v.id}`}>
                                      <span className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                        <span className="font-medium flex items-center gap-1">{v.name}{availEntry(svc.id, v.id)?.note && <MessageSquare className="w-3 h-3 text-amber-600" data-testid={`avail-note-badge-${svc.id}-${v.id}`} />}</span>
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                          <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                                          {alreadyAssigned ? "Al ingepland" : status.key === "none" ? "Niet opgegeven" : status.label}
                                        </span>
                                        {availEntry(svc.id, v.id)?.note && <span className="text-xs italic text-amber-700 w-full" data-testid={`avail-note-board-${svc.id}-${v.id}`}>“{availEntry(svc.id, v.id).note}”</span>}
                                      </span>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                      {svc.slots.length === 0 && <p className="text-xs text-muted-foreground">Geen functies gedefinieerd.</p>}
                    </div>
                    <AvailabilityReminder service={svc} source="board" />
                  </Card>
        )} />
        </div>
      </div>

      {/* Override dialog */}
      <Dialog open={!!override} onOpenChange={(o) => !o && setOverride(null)}>
        <DialogContent data-testid="override-dialog">
          <DialogHeader><DialogTitle>Harde regel overtreden</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {override?.warnings.map((w, i) => {
              const cfg = WARN[w.level] || WARN.warning;
              return (
                <div key={i} className={`flex items-start gap-2 text-sm border rounded-lg p-2.5 ${cfg.cls}`}>
                  <cfg.Icon className="w-4 h-4 shrink-0 mt-0.5" /> {w.message}
                </div>
              );
            })}
            <p className="text-sm text-muted-foreground">Als planner kun je deze regel overschrijven. De actie wordt geregistreerd.</p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOverride(null)} disabled={assigning} data-testid="override-cancel">Annuleren</Button>
            <Button variant="destructive" onClick={() => doAssign(override.payload, true)} disabled={assigning} data-testid="override-confirm">{assigning ? "Bezig met inplannen…" : "Toch inplannen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposals dialog */}
      <Dialog open={!!proposals} onOpenChange={(o) => !o && setProposals(null)}>
        <DialogContent className="max-w-2xl" data-testid="proposals-dialog">
          <DialogHeader><DialogTitle>Planningsvoorstel ({proposals?.length || 0})</DialogTitle></DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-1.5">
            {proposals?.length === 0 && <p className="text-sm text-muted-foreground">Geen voorstellen.</p>}
            {proposals?.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm border rounded-lg p-2.5" data-testid={`proposal-${i}`}>
                <div>
                  <span className="font-medium">{p.user_name}</span> → {p.service_name} · {p.service_type_name}
                  <p className="text-xs text-muted-foreground">{fmtDateLong(p.date)} · {p.start_time}–{p.end_time}</p>
                </div>
                <StatusBadge status={p.availability_status} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setProposals(null)}>Sluiten</Button>
            <Button onClick={applyProposals} disabled={busy || !proposals?.length} data-testid="apply-proposals">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Voorstel toepassen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
